import React from "react";
import { Box, HStack, Text, VStack, IconButton } from "@chakra-ui/react";
import { FaRegTrashAlt } from "react-icons/fa";
import { SupplierPartForDelivery } from "../../types";
import { estimateSectionHeaderProps } from "../../utils/estimateSectionHeaderStyles";

interface SplitPartsListProps {
  parts: SupplierPartForDelivery[];
  canRemoveParts?: boolean;
  isRemoveDisabled?: boolean;
  onRemovePart?: (partIndex: number) => void;
  /** Height cap before the list scrolls. */
  maxH?: string;
}

/** "Part Details / Qty" list of a split, with optional per-part remove. */
export const SplitPartsList: React.FC<SplitPartsListProps> = ({
  parts,
  canRemoveParts = false,
  isRemoveDisabled = false,
  onRemovePart,
  maxH = "180px",
}) => (
  <>
    <HStack mb={2} spacing={4}>
      <Text {...estimateSectionHeaderProps} flex="1">
        Part Details
      </Text>
      <Text {...estimateSectionHeaderProps} w="72px" textAlign="right">
        Qty
      </Text>
      {canRemoveParts ? <Box w="28px" /> : null}
    </HStack>

    <Box
      maxH={maxH}
      overflowY="auto"
      pr={1}
      css={{
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          background: "#CBD5E0",
          borderRadius: "2px",
        },
      }}
    >
      <VStack align="stretch" spacing={2}>
        {parts.map((part, index) => {
          const cancelledQty = Math.max(
            0,
            (part.requestedQty ?? part.quantity) - part.quantity,
          );
          return (
            <HStack
              key={`${part.partNumber}-${index}`}
              spacing={4}
              pb={1}
              borderBottom="1px dashed"
              borderColor="gray.200"
              _last={{ borderBottom: "none" }}
            >
              <VStack align="start" spacing={0} flex="1">
                <Text
                  fontSize="0.8rem"
                  color="gray.700"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="700"
                  lineHeight="1"
                >
                  {part.partNumber}
                </Text>
                <Text
                  fontSize="0.8rem"
                  color="gray.600"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="500"
                  mt="1"
                  lineHeight="1.1"
                  wordBreak="break-all"
                >
                  {part.partName}
                </Text>
              </VStack>
              <VStack spacing={0} w="72px" align="flex-end">
                <Text
                  fontSize="0.8rem"
                  color="gray.700"
                  fontFamily="'Nunito','Inter', sans-serif"
                  textAlign="right"
                  fontWeight="600"
                >
                  {part.quantity}
                </Text>
                {cancelledQty > 0 ? (
                  <Text
                    fontSize="0.65rem"
                    color="red.600"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="700"
                    textAlign="right"
                    lineHeight="1.1"
                  >
                    {cancelledQty} cancelled
                  </Text>
                ) : null}
              </VStack>
              {canRemoveParts ? (
                <IconButton
                  aria-label={`Remove part ${part.partNumber}`}
                  icon={<FaRegTrashAlt size={14} />}
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  minW="20px"
                  w="20px"
                  h="20px"
                  p={0}
                  cursor="pointer"
                  isDisabled={isRemoveDisabled}
                  onClick={() => onRemovePart?.(index)}
                  _hover={{ color: "red.500", bg: "transparent" }}
                  _active={{ bg: "transparent" }}
                />
              ) : null}
            </HStack>
          );
        })}
      </VStack>
    </Box>
  </>
);
